import { type FC, useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { PageTitle } from "../../../components/PageTitle";
import {
  EMAIL_SENDERS,
  emailToNameMap,
} from "../../../constants/email-senders";
import {
  type RecipientPreviewItem,
  type UpdateDeliveryInput,
  useDeliveryApi,
  usePreviewRecipientsApi,
  useSendDeliveryApi,
  useUpdateDeliveryApi,
} from "../../../hooks/use-delivery-api";
import { useGroupsApi } from "../../../hooks/use-distribution-api";
import { useEmailTemplatesRestApi } from "../../../hooks/useEmailTemplatesRestApi";

type TimingOption = "now" | "scheduled";

export const DeliveryEditPage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Fetch existing delivery
  const {
    data: delivery,
    loading: deliveryLoading,
    error: deliveryError,
  } = useDeliveryApi(id || "");

  // API hooks
  const { data: groupsData } = useGroupsApi({ limit: 100 });
  const { data: templatesData } = useEmailTemplatesRestApi();
  const { updateDelivery, loading: updateLoading } = useUpdateDeliveryApi();
  const { sendDelivery, loading: sendLoading } = useSendDeliveryApi();
  const { previewRecipients } = usePreviewRecipientsApi();

  // Form state
  const [groupId, setGroupId] = useState("");
  const [fromEmail, setFromEmail] = useState("");
  const [includeSender, setIncludeSender] = useState(false);
  const [templateId, setTemplateId] = useState("");
  const [subject, setSubject] = useState("");
  const [bodyText, setBodyText] = useState("");
  const [timing, setTiming] = useState<TimingOption>("now");
  const [scheduledAt, setScheduledAt] = useState("");

  // Preview state
  const [, setPreviewItems] = useState<RecipientPreviewItem[]>([]);
  const [previewTotal, setPreviewTotal] = useState(0);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Confirmation modal
  const [showConfirm, setShowConfirm] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);

  // Initialize form from delivery data
  useEffect(() => {
    if (delivery && !initialized) {
      setGroupId(delivery.groupId || "");
      setFromEmail(delivery.fromEmail);
      setIncludeSender(delivery.includeSender);
      setSubject(delivery.subject);
      setBodyText(delivery.bodyText);
      if (delivery.scheduledAt) {
        setTiming("scheduled");
        // Convert to datetime-local format
        const dt = new Date(delivery.scheduledAt);
        const local = new Date(dt.getTime() - dt.getTimezoneOffset() * 60000);
        setScheduledAt(local.toISOString().slice(0, 16));
      }
      setInitialized(true);
    }
  }, [delivery, initialized]);

  // Fetch recipient preview when group changes
  const fetchPreview = useCallback(
    async (gId: string) => {
      if (!gId) {
        setPreviewItems([]);
        setPreviewTotal(0);
        return;
      }
      setPreviewLoading(true);
      try {
        const result = await previewRecipients(gId);
        setPreviewItems(result.recipients);
        setPreviewTotal(result.total);
      } catch {
        setPreviewItems([]);
        setPreviewTotal(0);
      } finally {
        setPreviewLoading(false);
      }
    },
    [previewRecipients],
  );

  useEffect(() => {
    if (groupId && initialized) {
      fetchPreview(groupId);
    }
  }, [groupId, initialized, fetchPreview]);

  // Load template
  const handleTemplateLoad = () => {
    if (!templateId || !templatesData?.emailTemplates?.items) return;
    const tmpl = templatesData.emailTemplates.items.find(
      t => t.id === templateId,
    );
    if (tmpl) {
      setSubject(tmpl.subject || "");
      setBodyText(tmpl.body || "");
    }
  };

  const fromName = emailToNameMap[fromEmail] || "";

  const validate = (): boolean => {
    if (!groupId) {
      setFormError("配信先グループを選択してください");
      return false;
    }
    if (!fromEmail) {
      setFormError("送信者を選択してください");
      return false;
    }
    if (!subject.trim()) {
      setFormError("件名を入力してください");
      return false;
    }
    if (!bodyText.trim()) {
      setFormError("本文を入力してください");
      return false;
    }
    if (timing === "scheduled" && !scheduledAt) {
      setFormError("予約日時を指定してください");
      return false;
    }
    setFormError(null);
    return true;
  };

  // Save (update draft)
  const handleSave = async () => {
    if (!id || !groupId || !fromEmail || !subject.trim() || !bodyText.trim()) {
      setFormError("必須項目を入力してください");
      return;
    }
    setFormError(null);

    const input: UpdateDeliveryInput = {
      groupId,
      fromEmail,
      fromName: fromName || undefined,
      subject: subject.trim(),
      bodyText: bodyText.trim(),
      includeSender,
      scheduledAt: timing === "scheduled" ? `${scheduledAt}+09:00` : null,
    };

    try {
      await updateDelivery(id, input);
      navigate("/deliveries");
    } catch (e) {
      setFormError(e instanceof Error ? e.message : "更新に失敗しました");
    }
  };

  // Open confirmation modal
  const handleOpenConfirm = () => {
    if (!validate()) return;
    setShowConfirm(true);
  };

  // Execute send
  const handleSend = async () => {
    if (!id) return;
    setFormError(null);

    try {
      // First update
      const input: UpdateDeliveryInput = {
        groupId,
        fromEmail,
        fromName: fromName || undefined,
        subject: subject.trim(),
        bodyText: bodyText.trim(),
        includeSender,
        scheduledAt: timing === "scheduled" ? `${scheduledAt}+09:00` : null,
      };
      await updateDelivery(id, input);

      if (timing === "now") {
        await sendDelivery(id);
      }

      setShowConfirm(false);
      navigate("/deliveries");
    } catch (e) {
      setFormError(e instanceof Error ? e.message : "配信に失敗しました");
    }
  };

  if (deliveryLoading) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="flex justify-center py-12">
          <span className="loading loading-spinner loading-lg" />
        </div>
      </div>
    );
  }

  if (deliveryError || !delivery) {
    return (
      <div className="container mx-auto px-4 py-6">
        <Link to="/deliveries" className="btn btn-ghost btn-sm mb-4">
          &larr; 戻る
        </Link>
        <div className="alert alert-error">
          <span>{deliveryError?.message || "配信メールが見つかりません"}</span>
        </div>
      </div>
    );
  }

  if (delivery.status !== "draft" && delivery.status !== "scheduled") {
    return (
      <div className="container mx-auto px-4 py-6">
        <Link to="/deliveries" className="btn btn-ghost btn-sm mb-4">
          &larr; 戻る
        </Link>
        <div className="alert alert-warning">
          <span>この配信は編集できません（ステータス: {delivery.status}）</span>
        </div>
      </div>
    );
  }

  const groups = groupsData?.items || [];
  const templates = templatesData?.emailTemplates?.items || [];

  return (
    <div className="container mx-auto px-4 py-6 max-w-3xl">
      <div className="flex items-center gap-4 mb-6">
        <Link to="/deliveries" className="btn btn-ghost btn-sm">
          &larr; 戻る
        </Link>
        <PageTitle>配信メール編集</PageTitle>
      </div>

      {formError && (
        <div className="alert alert-error mb-4">
          <span>{formError}</span>
        </div>
      )}

      <div className="card bg-base-100 shadow-md">
        <div className="card-body space-y-4">
          {/* 配信先グループ */}
          <div className="form-control">
            <label className="label">
              <span className="label-text font-medium">
                配信先グループ <span className="text-error">*</span>
              </span>
            </label>
            <select
              className="select select-bordered"
              value={groupId}
              onChange={e => setGroupId(e.target.value)}
            >
              <option value="">グループを選択...</option>
              {groups.map(g => (
                <option key={g.id} value={g.id}>
                  {g.name} ({g.contactCount}件)
                </option>
              ))}
            </select>
            {groupId && (
              <div className="text-sm mt-1 text-base-content/60">
                {previewLoading ? (
                  <span className="loading loading-spinner loading-xs" />
                ) : (
                  `有効な宛先: ${previewTotal}件`
                )}
              </div>
            )}
          </div>

          {/* 送信者 */}
          <div className="form-control">
            <label className="label">
              <span className="label-text font-medium">
                送信者 <span className="text-error">*</span>
              </span>
            </label>
            <select
              className="select select-bordered"
              value={fromEmail}
              onChange={e => setFromEmail(e.target.value)}
            >
              <option value="">送信者を選択...</option>
              {EMAIL_SENDERS.map(s => (
                <option key={s.email} value={s.email}>
                  {s.name} ({s.email})
                </option>
              ))}
            </select>
          </div>

          {/* 自分にも送信 */}
          <div className="form-control">
            <label className="label cursor-pointer justify-start gap-2">
              <input
                type="checkbox"
                className="checkbox checkbox-sm"
                checked={includeSender}
                onChange={e => setIncludeSender(e.target.checked)}
              />
              <span className="label-text">自分にも送信する</span>
            </label>
          </div>

          {/* テンプレート */}
          <div className="form-control">
            <label className="label">
              <span className="label-text font-medium">テンプレート</span>
            </label>
            <div className="flex gap-2">
              <select
                className="select select-bordered flex-1"
                value={templateId}
                onChange={e => setTemplateId(e.target.value)}
              >
                <option value="">テンプレートを選択...</option>
                {templates.map(t => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="btn btn-outline btn-sm"
                onClick={handleTemplateLoad}
                disabled={!templateId}
              >
                読み込み
              </button>
            </div>
          </div>

          {/* 件名 */}
          <div className="form-control">
            <label className="label">
              <span className="label-text font-medium">
                件名 <span className="text-error">*</span>
              </span>
            </label>
            <input
              type="text"
              className="input input-bordered"
              value={subject}
              onChange={e => setSubject(e.target.value)}
              maxLength={500}
              placeholder="メールの件名を入力..."
            />
          </div>

          {/* 本文 */}
          <div className="form-control">
            <label className="label">
              <span className="label-text font-medium">
                本文 <span className="text-error">*</span>
              </span>
            </label>
            <textarea
              className="textarea textarea-bordered min-h-[200px]"
              value={bodyText}
              onChange={e => setBodyText(e.target.value)}
              placeholder="メールの本文を入力..."
            />
            <div className="text-xs mt-1 text-base-content/60">
              変数: {"{{COMPANY}}"} = 会社名, {"{{NAME}}"} = 担当者名
            </div>
          </div>

          {/* 配信タイミング */}
          <div className="form-control">
            <label className="label">
              <span className="label-text font-medium">配信タイミング</span>
            </label>
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  className="radio radio-sm"
                  name="timing"
                  checked={timing === "now"}
                  onChange={() => setTiming("now")}
                />
                <span>今すぐ配信</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  className="radio radio-sm"
                  name="timing"
                  checked={timing === "scheduled"}
                  onChange={() => setTiming("scheduled")}
                />
                <span>予約配信</span>
              </label>
              {timing === "scheduled" && (
                <input
                  type="datetime-local"
                  className="input input-bordered ml-6"
                  value={scheduledAt}
                  onChange={e => setScheduledAt(e.target.value)}
                />
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="card-body pt-0 flex-row justify-end gap-2">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={handleSave}
            disabled={updateLoading}
          >
            {updateLoading ? (
              <span className="loading loading-spinner loading-sm" />
            ) : (
              "保存"
            )}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleOpenConfirm}
            disabled={updateLoading || sendLoading}
          >
            確認画面へ
          </button>
        </div>
      </div>

      {/* 確認モーダル */}
      {showConfirm && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg">配信内容確認</h3>
            <div className="py-4 space-y-2">
              <div>
                <span className="font-medium">配信先: </span>
                {groups.find(g => g.id === groupId)?.name || "-"} (
                {previewTotal}件)
              </div>
              <div>
                <span className="font-medium">送信者: </span>
                {fromName || fromEmail}
              </div>
              <div>
                <span className="font-medium">件名: </span>
                {subject}
              </div>
              <div>
                <span className="font-medium">タイミング: </span>
                {timing === "now" ? "今すぐ配信" : `予約: ${scheduledAt}`}
              </div>
              <div className="divider" />
              <div className="bg-base-200 p-3 rounded-lg text-sm whitespace-pre-wrap max-h-40 overflow-y-auto">
                {bodyText}
              </div>
              {timing === "now" && (
                <div className="alert alert-warning text-sm">
                  <span>
                    {previewTotal}
                    件の宛先に配信されます。この操作は取り消せません。
                  </span>
                </div>
              )}
            </div>
            <div className="modal-action">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setShowConfirm(false)}
                disabled={updateLoading || sendLoading}
              >
                キャンセル
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSend}
                disabled={updateLoading || sendLoading}
              >
                {updateLoading || sendLoading ? (
                  <span className="loading loading-spinner loading-sm" />
                ) : timing === "now" ? (
                  "配信する"
                ) : (
                  "予約する"
                )}
              </button>
            </div>
          </div>
          <div
            className="modal-backdrop"
            onClick={() =>
              !(updateLoading || sendLoading) && setShowConfirm(false)
            }
          />
        </div>
      )}
    </div>
  );
};
